/**
 * Discovery / Bootstrap - Phase 2 part 2 wiring.
 * Source: GET /v1/discovery/bootstrap-progress
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Cloud, Server, ShieldCheck, KeyRound, Users, Network, Search, FileSpreadsheet, CheckCircle2, Loader2, Telescope, RotateCw, AlertTriangle } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  getBootstrapProgress, runBootstrapLevel, type BootstrapProgress, type BootstrapRunResponse,
  listBootstrapConnectors, createBootstrapConnector, deleteBootstrapConnector, runBootstrapConnector,
  type StoredBootstrapConnector, type CreateBootstrapConnectorInput,
} from '@/lib/api';
import { Plus, Play, Trash2 } from 'lucide-react';

const LEVEL_ICONS: Record<number, React.ComponentType<{ className?: string }>> = {
  1: Cloud, 2: Server, 3: ShieldCheck, 4: KeyRound, 5: Users, 6: Network, 7: Search, 8: FileSpreadsheet,
};

export function DiscoveryBootstrap() {
  const [data, setData] = useState<BootstrapProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Per-level state for the "Re-run level" action button. */
  const [running, setRunning] = useState<Record<number, boolean>>({});
  const [lastResult, setLastResult] = useState<BootstrapRunResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const refresh = (signal?: AbortSignal) =>
    getBootstrapProgress()
      .then((res) => !signal?.aborted && setData(res))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  async function onRerun(level: number) {
    setRunError(null);
    setRunning((s) => ({ ...s, [level]: true }));
    try {
      const res = await runBootstrapLevel(level);
      setLastResult(res);
      // Successful actuation: refresh the progress view.
      if (res.accepted) await refresh();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Re-run failed');
    } finally {
      setRunning((s) => ({ ...s, [level]: false }));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Setup checklist (8-level bootstrap)"
        subtitle="When one source can't reach a system, the next level takes over. Every asset gets a confidence score based on how many sources agree."
        badges={[
          { label: 'Aerospace-style fallback', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : data && data.summary.completeCount === 8
                ? [{ label: 'Fully bootstrapped', variant: 'success' as const }]
                : [{ label: `${data?.summary.completeCount ?? 0}/8 levels`, variant: 'glass' as const }]),
        ]}
      />

      {loading && <LoadingState caption="Loading bootstrap state…" />}
      {error && !loading && (
        <ErrorState
          title="Failed to load bootstrap state"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}
      {(lastResult || runError) && (
        <div className={`glass-lite border rounded-md p-3 mb-3 text-xs flex items-start gap-2 ${runError ? 'border-sev-high/40' : lastResult?.accepted ? 'border-success/40' : 'border-warning/40'}`}>
          {runError ? <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" /> : <RotateCw className="h-4 w-4 text-success shrink-0 mt-0.5" />}
          <div className="flex-1">
            {runError ? (
              <>
                <div className="font-medium">Bootstrap re-run failed</div>
                <div className="text-muted-foreground mt-0.5">{runError}</div>
              </>
            ) : lastResult ? (
              <>
                <div className="font-medium">
                  Level {lastResult.level} · {lastResult.status === 'queued' ? 'queued' : 'connector required'}
                </div>
                <div className="text-muted-foreground mt-0.5">{lastResult.message}</div>
              </>
            ) : null}
          </div>
          <Button size="sm" variant="glass" onClick={() => { setLastResult(null); setRunError(null); }}>Dismiss</Button>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Levels complete" value={data.summary.completeCount} icon={CheckCircle2} hint={`of ${data.summary.totalLevels} levels · ${(data.summary.completionPct * 100).toFixed(0)}% bootstrapped`} />
            <KpiCard label="Assets discovered" value={data.summary.assets} icon={Cloud} />
            <KpiCard label="Classified assets" value={data.summary.classifiedAssets} icon={Search} />
            <KpiCard label="Active canaries" value={data.summary.activeCanaries} icon={Telescope} />
          </div>

          <div className="space-y-3">
            {data.levels.map((level, i) => {
              const Icon = LEVEL_ICONS[level.level] ?? Cloud;
              return (
                <motion.div
                  key={level.level}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <GlassCard className={`p-4 flex items-center gap-4 ${!level.complete ? 'opacity-70' : ''}`}>
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${level.complete ? 'bg-success/15 text-success' : 'bg-muted/40 text-muted-foreground'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Level {level.level}</span>
                        <span className="text-sm font-medium">{level.title}</span>
                        {level.complete ? (
                          <Badge variant="success" className="text-[9px]">Active</Badge>
                        ) : (
                          <Badge variant="glass" className="text-[9px]">Pending</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{level.signal}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="glass"
                      disabled={running[level.level]}
                      onClick={() => onRerun(level.level)}
                      title={`Re-run level ${level.level} (POST /v1/discovery/bootstrap/run/${level.level})`}
                    >
                      {running[level.level] ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                      Re-run
                    </Button>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          <SavedConnectors onRan={() => refresh()} />

          <GlassCard className="p-5 mt-6 flex items-start gap-3">
            <Telescope className="h-5 w-5 text-info shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">How completeness is computed</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Each level&rsquo;s state is derived from concrete signals: connector counts,
                classified-asset counts, active canary counts, edge-pod heartbeats. Higher
                levels (2&ndash;6) unlock as you save + run their connectors below, or connect
                more data sources&nbsp;&mdash; see <Link to="/admin/integrations" className="text-primary hover:underline">Integrations</Link>
                {' '}and <Link to="/admin/edge-fleet" className="text-primary hover:underline">Edge Fleet</Link>.
              </p>
            </div>
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}

// ── Saved bootstrap connectors (L2-L6) ───────────────────────────────
// Save a connector once (secret encrypted server-side), then run it.

const KIND_FOR_LEVEL: Record<number, StoredBootstrapConnector['kind']> = {
  2: 'servicenow_cmdb', 3: 'generic_rest', 4: 'scim', 5: 'generic_rest', 6: 'generic_rest',
};
const LEVEL_LABEL: Record<number, string> = {
  2: 'CMDB (ServiceNow)', 3: 'EDR / endpoint', 4: 'SSO / SCIM', 5: 'HR / workforce', 6: 'Network',
};

function SavedConnectors({ onRan }: { onRan: () => void }) {
  const [connectors, setConnectors] = useState<StoredBootstrapConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [showForm, setShowForm] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = () =>
    listBootstrapConnectors()
      .then((r) => setConnectors(r.connectors))
      .catch((e) => setErr(e instanceof Error ? e.message : 'load failed'))
      .finally(() => setLoading(false));

  useEffect(() => { void load(); }, []);

  async function onRun(id: string) {
    setNote(null);
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      const res = await runBootstrapConnector(id);
      setNote(
        res.status === 'ok' && res.result
          ? `Ran: ${res.result.created} created, ${res.result.updated} updated, ${res.result.skipped} skipped${res.errors.length ? ` (${res.errors.length} warnings)` : ''}`
          : `Run failed: ${res.errors.join('; ') || 'unknown error'}`,
      );
      await load();
      onRan();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'run failed');
    } finally {
      setBusy((s) => ({ ...s, [id]: false }));
    }
  }

  async function onDelete(id: string) {
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      await deleteBootstrapConnector(id);
      await load();
    } finally {
      setBusy((s) => ({ ...s, [id]: false }));
    }
  }

  return (
    <GlassCard className="p-5 mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium">Saved connectors (Levels 2&ndash;6)</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Save a CMDB / EDR / SCIM / HR / network connector once&nbsp;&mdash; secrets are
            AES-256-GCM encrypted&nbsp;&mdash; then run it to ingest assets.
          </p>
        </div>
        <Button size="sm" variant="glass" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-3 w-3" /> Add connector
        </Button>
      </div>

      {note && (
        <div className="glass-lite border border-info/40 rounded-md p-2 mb-3 text-xs flex items-start gap-2">
          <span className="flex-1">{note}</span>
          <Button size="sm" variant="glass" onClick={() => setNote(null)}>Dismiss</Button>
        </div>
      )}

      {showForm && <AddConnectorForm onSaved={() => { setShowForm(false); void load(); }} />}

      {loading && <p className="text-xs text-muted-foreground">Loading connectors&hellip;</p>}
      {err && !loading && <p className="text-xs text-sev-high">{err}</p>}
      {!loading && connectors.length === 0 && (
        <p className="text-xs text-muted-foreground">No saved connectors yet. Click &ldquo;Add connector&rdquo;.</p>
      )}

      <div className="space-y-2">
        {connectors.map((c) => (
          <div key={c.id} className="glass-lite border rounded-md p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="glass" className="text-[9px]">L{c.level} · {LEVEL_LABEL[c.level] ?? c.kind}</Badge>
                <span className="text-sm font-medium truncate">{c.displayName}</span>
                {c.hasSecret && <KeyRound className="h-3 w-3 text-muted-foreground" />}
                {c.lastRunStatus && (
                  <Badge variant={c.lastRunStatus === 'ok' ? 'success' : 'warning'} className="text-[9px]">
                    last: {c.lastRunStatus}
                  </Badge>
                )}
              </div>
              {c.lastRunSummary && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {String((c.lastRunSummary as Record<string, unknown>).created ?? 0)} created,{' '}
                  {String((c.lastRunSummary as Record<string, unknown>).updated ?? 0)} updated
                  {c.lastRunAt ? ` · ${new Date(c.lastRunAt).toLocaleString()}` : ''}
                </p>
              )}
            </div>
            <Button size="sm" variant="glass" disabled={busy[c.id]} onClick={() => onRun(c.id)}>
              {busy[c.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run
            </Button>
            <Button size="sm" variant="glass" disabled={busy[c.id]} onClick={() => onDelete(c.id)} title="Delete">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function AddConnectorForm({ onSaved }: { onSaved: () => void }) {
  const [level, setLevel] = useState(2);
  const [displayName, setDisplayName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    setSaving(true);
    try {
      const kind = KIND_FOR_LEVEL[level] ?? 'generic_rest';
      // Build the per-kind config + secret shape the backend adapters expect.
      let config: Record<string, unknown>;
      let secretObj: Record<string, unknown> | undefined;
      if (kind === 'servicenow_cmdb') {
        config = { instanceUrl: url, auth: { method: 'basic', username: 'svc' } };
        secretObj = secret ? { password: secret } : undefined;
      } else if (kind === 'scim') {
        config = { baseUrl: url, resource: 'Groups' };
        secretObj = secret ? { token: secret } : undefined;
      } else {
        config = { url, recordsPath: '', fieldMap: { externalRef: 'id', displayName: 'name' } };
        secretObj = secret ? { token: secret } : undefined;
      }
      const input: CreateBootstrapConnectorInput = { level, kind, displayName, config };
      if (secretObj) input.secret = secretObj;
      await createBootstrapConnector(input);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="glass-lite border rounded-md p-3 mb-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-muted-foreground">Level</span>
          <select
            className="w-full mt-0.5 bg-background border rounded px-2 py-1 text-sm"
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
          >
            {[2, 3, 4, 5, 6].map((l) => (
              <option key={l} value={l}>L{l} · {LEVEL_LABEL[l]}</option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground">Display name</span>
          <input className="w-full mt-0.5 bg-background border rounded px-2 py-1 text-sm"
            value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Prod ServiceNow" />
        </label>
      </div>
      <label className="text-xs block">
        <span className="text-muted-foreground">Endpoint URL</span>
        <input className="w-full mt-0.5 bg-background border rounded px-2 py-1 text-sm"
          value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://acme.service-now.com" />
      </label>
      <label className="text-xs block">
        <span className="text-muted-foreground">Secret (token / password) &mdash; encrypted on save</span>
        <input type="password" className="w-full mt-0.5 bg-background border rounded px-2 py-1 text-sm"
          value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="••••••••" />
      </label>
      {error && <p className="text-xs text-sev-high">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" variant="default" disabled={saving || !displayName || !url} onClick={onSubmit}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Save connector
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        For EDR/HR/network (generic REST), the default field map is {`{id, name}`}; advanced
        field maps can be set via the API. ServiceNow uses basic auth (username &ldquo;svc&rdquo;
        by default) + the password above.
      </p>
    </div>
  );
}
